import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { addWarningNotification, getBalanceTypeById, isOrderMemberBalancePayment, isOrderPaymentRemovable, LoadingState, NotificationType, orderHasInProgressAction, removeOrderPayment } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { OrderPaymentType } from '@vista-digital/ocapi-types/v1';
import { observer } from 'mobx-react';
import { useCallback, useContext, useState } from 'react';
import { useI18n } from '../../../context/occ/hooks/use-i18n';
import { useOccContext } from '../../../context/occ/hooks/use-occ-context';
import { Icon } from '../../../subcomponents/icon';
import { getFormattedMemberBalance } from '../../../utils/get-formatted-member-balance';
import { getIdForComponentNotification } from '../../../utils/get-id-for-component-notification';
import { OrderPaymentFormContextInstance } from '../types/context';
const b = bemClassBuilder('v-order-payment-form-payment');
export const OrderPaymentFormPayment = observer(props => {
    const { accessibilityText, displayText: { member: memberDisplayText } } = useOccContext();
    const i18n = useI18n();
    const { payment } = props;
    const { displayText, assets } = useContext(OrderPaymentFormContextInstance);
    const [removeLoadingState, setRemoveLoadingState] = useState(LoadingState.NotLoaded);
    const balanceType = isOrderMemberBalancePayment(payment) ? getBalanceTypeById(payment.balanceTypeId) : undefined;
    const handleRemoveButtonClicked = useCallback(async () => {
        try {
            setRemoveLoadingState(LoadingState.Loading);
            await removeOrderPayment(payment.id);
        }
        catch (error) {
            setRemoveLoadingState(LoadingState.Failure);
            addWarningNotification({
                id: getIdForComponentNotification('OrderPaymentFormPayment', NotificationType.Warning, payment.id),
                messages: [getFailedToRemovePaymentMessage(i18n, memberDisplayText, displayText, payment, balanceType)]
            });
        }
    }, [payment, memberDisplayText, displayText, i18n, balanceType]);
    const iconName = removeLoadingState === LoadingState.Loading
        ? assets.removePaymentLoadingIconName
        : assets.removePaymentButtonIconName;
    const disabled = orderHasInProgressAction();
    if (payment.type === OrderPaymentType.MemberBalance && !balanceType) {
        throw new Error(`Failed to render OrderPaymentFormPayment. Could not get balance type with ID: '${payment.balanceTypeId}`);
    }
    const details = getPaymentDetails(i18n, memberDisplayText, displayText, payment, balanceType);
    return (_jsxs("li", Object.assign({ className: b.block() }, { children: [isOrderPaymentRemovable(payment) && (_jsxs(_Fragment, { children: [_jsx("button", Object.assign({ className: b.element('remove-button', { disabled }), "aria-label": getRemoveButtonAriaLabel(i18n, memberDisplayText, accessibilityText, payment, balanceType), onClick: handleRemoveButtonClicked, disabled: disabled }, { children: _jsx(Icon, { name: iconName, className: b.element('remove-icon', { disabled }) }) })), removeLoadingState === LoadingState.Failure && (_jsx(Icon, { name: assets.failedToRemovePaymentIconName, className: b.element('remove-failed-icon') }))] })), _jsx("span", Object.assign({ className: b.element('details') }, { children: details })), _jsx("span", Object.assign({ className: b.element('value') }, { children: i18n.currency(-payment.value) }))] })));
});
OrderPaymentFormPayment.displayName = 'OrderPaymentFormPayment';
const getPaymentDetails = (i18n, memberDisplayText, displayText, payment, balanceType) => {
    switch (payment.type) {
        case OrderPaymentType.GiftCard:
            return i18n.template(displayText.maskedGiftCardNumberFormat, {
                maskedCardNumber: payment.maskedCardNumber
            });
        case OrderPaymentType.MemberBalance:
            return i18n.template(displayText.memberBalancePaymentFormat, {
                units: getFormattedMemberBalance(i18n, memberDisplayText, payment.units, balanceType === null || balanceType === void 0 ? void 0 : balanceType.unitName),
                balanceTypeName: i18n.translatable(balanceType === null || balanceType === void 0 ? void 0 : balanceType.name) || memberDisplayText.defaultBalanceTypeName
            });
        default:
            return undefined;
    }
};
const getRemoveButtonAriaLabel = (i18n, memberDisplayText, accessibilityText, payment, balanceType) => {
    switch (payment.type) {
        case OrderPaymentType.GiftCard:
            return i18n.template(accessibilityText.removeGiftCardButtonAriaLabelFormat, {
                maskedCardNumber: payment.maskedCardNumber
            });
        case OrderPaymentType.MemberBalance:
            return i18n.template(accessibilityText.removeMemberBalancePaymentButtonAriaLabelFormat, {
                units: getFormattedMemberBalance(i18n, memberDisplayText, payment.units, balanceType === null || balanceType === void 0 ? void 0 : balanceType.unitName)
            });
        default:
            return undefined;
    }
};
const getFailedToRemovePaymentMessage = (i18n, memberDisplayText, displayText, payment, balanceType) => {
    switch (payment.type) {
        case OrderPaymentType.GiftCard:
            return i18n.template(displayText.failedToRemoveGiftCardErrorMessage, {
                maskedCardNumber: payment.maskedCardNumber
            });
        case OrderPaymentType.MemberBalance:
            return i18n.template(displayText.failedToRemoveMemberBalancePaymentErrorMessage, {
                units: getFormattedMemberBalance(i18n, memberDisplayText, payment.units, balanceType === null || balanceType === void 0 ? void 0 : balanceType.unitName)
            });
        default:
            return undefined;
    }
};
